/*
================================================================================
FILE: AIControlRoomAdaptive.jsx (MOBILE-FRIENDLY + DATA HOOKS + FILTERS)
Location: src/pages/AIControlRoomAdaptive/AIControlRoomAdaptive.jsx
================================================================================
*/

import React, { useState, useEffect, useMemo } from 'react';
import { 
  Activity, 
  TrendingUp, 
  MessageCircle,
  Zap,
  BarChart3,
  Flame,
  DollarSign,
  XCircle,
  Banknote,
  Phone,
  Loader2,
  ChevronDown,
  Building2,
  Megaphone,
  Calendar,
  UserX,
  Clock,
  MessageSquare,
  UserCheck,
  Timer
} from 'lucide-react';
import clsx from 'clsx';
import { useControlRoomStore } from './utils/controlRoomStore';
import { useMetricsPolling } from './hooks/useMetricsPolling';
import { useTenants } from './hooks/useTenants';
import { useCampaigns } from './hooks/useCampaigns';
import { useAuth } from '../../context/AuthContext';
import supabase from '../../lib/supabaseClient';
import IntelCard from './components/IntelCard';
import CommandBar  from './components/CommandBar';
import PriorityQueue from './components/PriorityQueue';
import SystemHealth from './components/SystemHealth';

// ============================================
// MOBILE PANEL NAVIGATION (TABS)
// ============================================
const MobileNav = ({ activePanel, setActivePanel }) => (
  <div className="flex lg:hidden border-b border-gray-200 bg-white sticky top-0 z-30">
    {[
      { key: 'queue', label: 'Queue' },
      { key: 'intel', label: 'Intelligence' },
      { key: 'health', label: 'Health' }
    ].map(({ key, label }) => (
      <button
        key={key}
        onClick={() => setActivePanel(key)}
        className={clsx(
          "flex-1 py-3 text-xs font-semibold uppercase tracking-wide transition-colors",
          activePanel === key 
            ? "text-orange-600 border-b-2 border-orange-500 bg-orange-50/50" 
            : "text-gray-500 hover:text-gray-700 hover:bg-gray-50"
        )}
      >
        {label}
      </button>
    ))}
  </div>
);

// ============================================
// DATE RANGE HELPER
// ============================================
const getDateRangeLabel = (dateRange) => {
  const labels = {
    'today': 'Today',
    'yesterday': 'Yesterday',
    'last7': 'Last 7 days',
    'last30': 'Last 30 days',
    'thisMonth': 'This month',
    'lastMonth': 'Last month',
    'allTime': 'All time'
  };
  return labels[dateRange] || dateRange;
};

const getDateRangeContext = (dateRange) => {
  const contexts = {
    'today': 'today',
    'yesterday': 'yesterday',
    'last7': 'in last 7 days',
    'last30': 'in last 30 days',
    'thisMonth': 'this month',
    'lastMonth': 'last month',
    'allTime': 'all time'
  };
  return contexts[dateRange] || '';
};

// Build a card trend badge from a REAL period-over-period value.
// `trend` text always shows the actual signed % so the number is honest.
// `trendUp` means "this change is good" and drives only the COLOUR (green / red) in IntelCard.
// `trendSign` is the direction of the number and drives the ARROW, so the arrow always agrees with
// the sign beside it. (Before 2026-10-03 the arrow also followed good/bad, so a falling opt-out rate
// showed a green ↑ next to -18.3%, which the first outside reviewer read as a bug.)
const buildTrend = (value, { higherIsBetter = true, zeroIsNoBaseline = false } = {}) => {
  const v = Math.round((Number(value) || 0) * 10) / 10;
  // get_control_room_metrics returns exactly 0 for a trend whenever the previous period had no value to compare with
  // (ELSE 0 in the SQL), which this card would show as a green "0%" = "no change". For rate and time cards an exactly
  // equal real change is practically impossible, so 0 there is read as "nothing to compare against".
  if (zeroIsNoBaseline && v === 0) return { trend: 'no prior data', trendUp: true, trendSign: 0, noBaseline: true };
  return {
    trend: `${v > 0 ? '+' : ''}${v}%`,
    trendUp: higherIsBetter ? v >= 0 : v <= 0,
    trendSign: v > 0 ? 1 : v < 0 ? -1 : 0
  };
};

// ============================================
// MAIN COMPONENT
// ============================================
const AIControlRoomAdaptive = ({ selectedTenantId: initialTenantId = 'all' }) => {
  const { viewState, currentState, channel, setChannel } = useControlRoomStore();
  const { user } = useAuth();
  
  // Filter state — only global_admin defaults to 'all' (multi-tenant overview).
  // partner_admin and below default to their own tenant; they can still switch
  // via the tenant filter (partner_admins with child tenants) if available.
  const isAdmin = user?.role === 'partner_admin' || user?.role === 'global_admin';
  const [selectedTenant, setSelectedTenant] = useState(() => {
    if (initialTenantId !== 'all') return initialTenantId;
    return user?.role === 'global_admin' ? 'all' : (user?.tenant_id || null);
  });
  const [selectedCampaign, setSelectedCampaign] = useState('all');
  const [selectedDateRange, setSelectedDateRange] = useState('last7');

  // Mobile panel toggle
  const [activePanel, setActivePanel] = useState('intel');

  // Update tenant when user loads (only if not yet resolved)
  useEffect(() => {
    if (user?.tenant_id && !selectedTenant) {
      setSelectedTenant(user.role === 'global_admin' ? 'all' : user.tenant_id);
    }
  }, [user?.tenant_id]);

  // Determine if user can see tenant filter (partner/global admin only)
  const showTenantFilter = isAdmin;
  
  // Fetch tenants and campaigns
  const { data: tenants = [] } = useTenants({
    enabled: showTenantFilter,
    role: user?.role,
    tenantId: user?.tenant_id
  });
  const { data: campaigns = [] } = useCampaigns(selectedTenant);

  // Gate the Web channel toggle exactly like the left-nav Web Chat item (Layout.jsx):
  // only show it when the selected tenant actually has a campaign wired to the web
  // widget (tenants.widget_campaign_id). Without one there's no web data, so the
  // toggle just confuses customers. global_admin always sees it (support access),
  // matching the nav. For the 'all tenants' overview we can't gate on one tenant,
  // so it's admin-only there too.
  const [hasWebChatCampaign, setHasWebChatCampaign] = useState(false);
  useEffect(() => {
    if (!selectedTenant || selectedTenant === 'all') { setHasWebChatCampaign(false); return; }
    let cancelled = false;
    (async () => {
      try {
        const { data } = await supabase
          .from('tenants')
          .select('widget_campaign_id')
          .eq('id', selectedTenant)
          .maybeSingle();
        if (!cancelled) setHasWebChatCampaign(!!data?.widget_campaign_id);
      } catch (err) {
        console.error('control room web chat campaign check failed:', err);
      }
    })();
    return () => { cancelled = true; };
  }, [selectedTenant]);

  const showWebChannel = hasWebChatCampaign || user?.role === 'global_admin';

  // If the Web channel becomes unavailable (e.g. tenant switched to one without a
  // web campaign) while it's the active channel, fall back to SMS so we never show
  // an empty/irrelevant web view.
  useEffect(() => {
    if (!showWebChannel && channel === 'web') setChannel('sms');
  }, [showWebChannel, channel, setChannel]);

  // Pass filters to metrics polling
  const { data: metrics, isLoading, error } = useMetricsPolling({
    tenantId: selectedTenant,
    campaignId: selectedCampaign,
    dateRange: selectedDateRange,
    channel,
    enabled: !!selectedTenant
  });

  // Handle filter changes
  const handleTenantChange = (tenantId) => {
    setSelectedTenant(tenantId);
    setSelectedCampaign('all');
  };
  
  const handleCampaignChange = (campaignId) => {
    setSelectedCampaign(campaignId);
  };
  
  const handleDateRangeChange = (dateRange) => {
    setSelectedDateRange(dateRange);
  };

  // Build filter options
  const tenantOptions = [
    { value: 'all', label: 'All Tenants' },
    ...tenants.map(t => ({ value: t.id, label: t.name }))
  ];
  
  const campaignOptions = [
    { value: 'all', label: 'All Campaigns' },
    ...campaigns.map(c => ({ value: c.id, label: c.name }))
  ];
  
  const dateOptions = [
    { value: 'today', label: 'Today' },
    { value: 'yesterday', label: 'Yesterday' },
    { value: 'last7', label: 'Last 7 days' },
    { value: 'last30', label: 'Last 30 days' },
    { value: 'thisMonth', label: 'This month' },
    { value: 'lastMonth', label: 'Last month' },
    { value: 'allTime', label: 'All time' }
  ];

  // Card definitions with live data
  const cards = useMemo(() => {
    if (!metrics) return [];
    
    return [
      {
        id: 'hot-rate',
        class: 'card-hot-rate',
        label: 'Hot Lead Rate',
        value: `${metrics.hotLeadRate || 0}%`,
        context: 'Conversations → Hot',
        ...buildTrend(metrics.hotLeadTrend, { higherIsBetter: true, zeroIsNoBaseline: true }),
        color: '#EA580C',
        icon: Flame,
        chartData: Array.from({length: 12}, () => 20 + Math.random() * 80)
      },
      {
        id: 'response',
        class: 'card-response',
        label: channel === 'web' ? 'Engagement Rate' : 'Response Rate',
        value: `${metrics.responseRate || 0}%`,
        context: channel === 'web' ? 'Visitors who replied to AI' : 'Leads engaging with AI',
        ...buildTrend(metrics.responseTrend, { higherIsBetter: true, zeroIsNoBaseline: true }),
        color: '#3B82F6',
        icon: MessageSquare,
        chartData: Array.from({length: 12}, () => 20 + Math.random() * 80)
      },
      {
        id: 'pipeline',
        class: 'card-pipeline',
        label: 'Potential Pipeline Added',
        // Booked appointments only. A handoff is a hot lead now, but only a lead
        // holding a booked appointment is worth a deal's value on this card.
        // $1,292,000 used to read "$1292K"; from a million up it reads "$1.29M".
        value: (metrics.pipelineAdded || 0) >= 1000000
          ? `$${((metrics.pipelineAdded || 0) / 1000000).toFixed(2)}M`
          : `$${((metrics.pipelineAdded || 0) / 1000).toFixed(0)}K`,
        context: `${metrics.pipelineHotLeads || 0} booked · ${getDateRangeLabel(selectedDateRange)}`,
        ...buildTrend(metrics.pipelineTrend, { higherIsBetter: true }),
        color: '#8B5CF6',
        icon: DollarSign,
        chartData: Array.from({length: 12}, () => 20 + Math.random() * 80)
      },
      {
        id: 'booked',
        class: 'card-accuracy',
        label: 'Calls Booked',
        // REAL: hot AND booking_completed, from get_control_room_metrics (the same number the pipeline card prices).
        // Replaced the old "AI Accuracy" tile, which was a placeholder that always read 0% (Tom, 2026-10-03).
        value: String(metrics.pipelineHotLeads || 0),
        context: `Booked by the AI · ${getDateRangeLabel(selectedDateRange)}`,
        // Period over period from the RPC's own previous-period count; no previous bookings means no trend to show.
        ...buildTrend(
          (metrics.prevPipelineHotLeads || 0) > 0
            ? ((metrics.pipelineHotLeads || 0) - metrics.prevPipelineHotLeads) / metrics.prevPipelineHotLeads * 100
            : 0,
          { higherIsBetter: true }
        ),
        color: '#14B8A6',
        icon: Calendar,
        chartData: Array.from({length: 12}, () => 20 + Math.random() * 80)
      },
{
  id: 'volume',
  class: 'card-volume',
  label: 'Message Volume',
  value: metrics.messageVolume?.toLocaleString() || '0',
  context: channel === 'web' ? `Visitor messages ${getDateRangeContext(selectedDateRange)}` : `Messages ${getDateRangeContext(selectedDateRange)}`,
        ...buildTrend(metrics.volumeTrend, { higherIsBetter: true }),
        color: '#F59E0B',
        icon: BarChart3,
        chartData: Array.from({length: 12}, () => 20 + Math.random() * 80)
      },
      {
        id: 'time-hot',
        label: 'Avg Time to Hot',
        value: metrics.avgTimeToHotDisplay || '0h',
        context: `Based on ${metrics.totalHotLeads || 0} lead${metrics.totalHotLeads === 1 ? '' : 's'} ${getDateRangeContext(selectedDateRange)}`,
        ...buildTrend(metrics.timeToHotTrend, { higherIsBetter: false, zeroIsNoBaseline: true }),
        color: '#16A34A',
        icon: Zap,
        chartData: Array.from({length: 12}, () => 20 + Math.random() * 80)
      },
      // SMS-only: Opt-Out Rate / Web-only: Identity Capture Rate
      channel === 'web'
        ? {
            id: 'identity-capture',
            class: 'card-identity',
            label: 'Identity Capture',
            value: `${metrics.identityCaptureRate || 0}%`,
            context: 'Visitors who shared contact info',
            ...buildTrend(metrics.identityCaptureTrend, { higherIsBetter: true }),
            color: '#8B5CF6',
            icon: UserCheck,
            chartData: Array.from({length: 12}, () => 20 + Math.random() * 80)
          }
        : {
            id: 'optout',
            class: 'card-optout',
            label: 'Opt-Out Rate',
            value: `${Math.round((Number(metrics.optOutRate) || 0) * 10) / 10}%`,
            context: `Of leads contacted ${getDateRangeContext(selectedDateRange)}`,
            ...buildTrend(metrics.optOutTrend, { higherIsBetter: false, zeroIsNoBaseline: true }),
            color: '#DC2626',
            icon: UserX,
            chartData: Array.from({length: 12}, () => 20 + Math.random() * 80)
          },
      {
        id: 'cost',
        class: 'card-cost',
        label: 'Cost per Hot Lead',
        value: `$${metrics.costPerHotLead || 0}`,
        context: 'Average efficiency',
        ...buildTrend(metrics.costTrend, { higherIsBetter: false }),
        color: '#10B981',
        icon: Banknote,
        chartData: Array.from({length: 12}, () => 20 + Math.random() * 80)
      },
      // SMS-only: Sales Response Time / Web-only: Avg Session Duration
      channel === 'web'
        ? {
            id: 'session-duration',
            class: 'card-session',
            label: 'Avg Session Duration',
            value: `${metrics.avgSessionDuration || 0}m`,
            context: 'Time spent per conversation',
            ...buildTrend(metrics.sessionDurationTrend, { higherIsBetter: true }),
            color: '#6366F1',
            icon: Timer,
            chartData: Array.from({length: 12}, () => 20 + Math.random() * 80)
          }
        : {
            id: 'sales-response',
            class: 'card-sales-response',
            label: 'Sales Response',
            value: `${metrics.salesResponseTime || 0}m`,
            context: 'Time to call hot leads',
            ...buildTrend(metrics.salesResponseTrend, { higherIsBetter: false, zeroIsNoBaseline: true }),
            color: '#F59E0B',
            icon: Phone,
            chartData: Array.from({length: 12}, () => 20 + Math.random() * 80)
          }
    ];
  }, [metrics, selectedDateRange, channel]);

  const stateLabels = {
    'default': 'Balanced View',
    'hot-spike': 'Hot Lead Spike',
    'high-volume': 'High Activity',
    'quiet': 'Quiet Time'
  };

  // Determine which cards to show on mobile based on state
  const getMobileCards = () => {
    const priorityCards = {
      'default': ['hot-rate', 'response', 'pipeline', 'booked', 'volume', 'time-hot'],
      'hot-spike': ['hot-rate', 'time-hot', 'pipeline', 'response', 'booked'],
      'high-volume': ['volume', 'response', 'hot-rate', 'time-hot', 'booked'],
      'quiet': ['hot-rate', 'pipeline', 'booked']
    };
    return priorityCards[viewState] || priorityCards.default;
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-full">
        <Loader2 className="w-8 h-8 animate-spin text-orange-500" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center p-8">
          <h2 className="text-lg font-semibold text-red-600 mb-2">Error loading metrics</h2>
          <p className="text-gray-600">{error.message}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col bg-gray-50 h-full">
      <CommandBar
        tenants={tenants}
        campaigns={campaigns}
        selectedTenant={selectedTenant}
        selectedCampaign={selectedCampaign}
        selectedDateRange={selectedDateRange}
        onTenantChange={handleTenantChange}
        onCampaignChange={handleCampaignChange}
        onDateRangeChange={handleDateRangeChange}
        showTenantFilter={showTenantFilter}
        showWebChannel={showWebChannel}
      />
      {/* Mobile Panel Navigation */}
      <MobileNav activePanel={activePanel} setActivePanel={setActivePanel} />

      {/* Main Layout */}
      <div className="flex-1 flex flex-col lg:grid lg:grid-cols-[320px_1fr_300px] xl:grid-cols-[360px_1fr_340px] overflow-hidden min-h-0">

        {/* Left Panel - Priority Queue */}
<div className={clsx(
  "lg:flex lg:flex-col",
  activePanel === 'queue' ? "flex flex-col flex-1" : "hidden"
)} style={{ maxHeight: 'calc(100vh - 120px)' }}>
  <PriorityQueue selectedTenant={selectedTenant} selectedCampaign={selectedCampaign} selectedDateRange={selectedDateRange} channel={channel} isGlobalAdmin={showTenantFilter} />
</div>

        {/* Center Panel - Adaptive Intelligence Surface */}
        <div className={clsx(
          "bg-gray-50 overflow-hidden relative",
          activePanel === 'intel' ? "flex flex-col flex-1 lg:block" : "hidden lg:block"
        )}>
          {/* State Indicator - Desktop only */}
          <div className="hidden lg:block absolute top-6 right-6 z-10 px-4 py-2 bg-white border border-gray-200 rounded-lg text-xs font-semibold text-gray-600 shadow-sm">
            {stateLabels[viewState]}
          </div>

          {/* Mobile Intelligence Grid */}
          <div className="lg:hidden h-full overflow-y-auto p-3 sm:p-4">
            <div className="grid grid-cols-2 gap-2 sm:gap-3">
              {getMobileCards().map(cardId => {
                const card = cards.find(c => c.id === cardId);
                return card ? (
                  <IntelCard
                    key={card.id}
                    card={card}
                    isExpanded={false}
                    isFaded={false}
                  />
                ) : null;
              })}
            </div>
          </div>

          {/* Desktop Intelligence Grid */}
          <div className="hidden lg:block relative w-full h-full p-4">

            {/* Hot Spike State Layout */}
{viewState === 'hot-spike' && (
  <div className="grid grid-cols-4 grid-rows-3 gap-3 h-full">
    <div className="col-span-2 row-span-2 h-full">
      <IntelCard card={cards.find(c => c.id === 'hot-rate')} isExpanded={true} isFaded={false} />
    </div>
    <IntelCard card={cards.find(c => c.id === 'time-hot')} isExpanded={false} isFaded={false} />
    <IntelCard card={cards.find(c => c.id === 'pipeline')} isExpanded={false} isFaded={false} />
    <IntelCard card={cards.find(c => c.id === 'response')} isExpanded={false} isFaded={false} />
    <IntelCard card={cards.find(c => c.id === 'booked')} isExpanded={false} isFaded={false} />
    <IntelCard card={cards.find(c => c.id === 'volume')} isExpanded={false} isFaded={true} />
    <IntelCard card={cards.find(c => c.id === 'sales-response')} isExpanded={false} isFaded={true} />
    <IntelCard card={cards.find(c => c.id === 'cost')} isExpanded={false} isFaded={true} />
    <IntelCard card={cards.find(c => c.id === 'optout')} isExpanded={false} isFaded={true} />
  </div>
)}

            {/* High Volume State Layout */}
{viewState === 'high-volume' && (
  <div className="grid grid-cols-4 grid-rows-3 gap-3 h-full">
    <div className="col-span-2 row-span-2 h-full">
      <IntelCard card={cards.find(c => c.id === 'volume')} isExpanded={true} isFaded={false} />
    </div>
    <IntelCard card={cards.find(c => c.id === 'response')} isExpanded={false} isFaded={false} />
    <IntelCard card={cards.find(c => c.id === 'booked')} isExpanded={false} isFaded={false} />
    <IntelCard card={cards.find(c => c.id === 'time-hot')} isExpanded={false} isFaded={false} />
    <IntelCard card={cards.find(c => c.id === 'hot-rate')} isExpanded={false} isFaded={false} />
    <IntelCard card={cards.find(c => c.id === 'pipeline')} isExpanded={false} isFaded={true} />
    <IntelCard card={cards.find(c => c.id === 'cost')} isExpanded={false} isFaded={true} />
    <IntelCard card={cards.find(c => c.id === 'sales-response')} isExpanded={false} isFaded={true} />
    <IntelCard card={cards.find(c => c.id === 'optout')} isExpanded={false} isFaded={true} />
  </div>
)}

            {/* Quiet State Layout */}
            {viewState === 'quiet' && (
              <div className="grid grid-cols-2 grid-rows-[2fr_1fr] gap-5 p-10 h-full">
                <IntelCard card={cards.find(c => c.id === 'hot-rate')} isExpanded={false} isFaded={false} />
                <IntelCard card={cards.find(c => c.id === 'pipeline')} isExpanded={false} isFaded={false} />
                <div className="col-span-2">
                  <IntelCard card={cards.find(c => c.id === 'booked')} isExpanded={false} isFaded={false} />
                </div>
              </div>
            )}

            {/* Default State Layout */}
            {viewState === 'default' && (
              <div className="grid grid-cols-3 grid-rows-3 gap-3 h-full">
                {cards.map(card => (
                  <IntelCard key={card.id} card={card} isExpanded={false} isFaded={false} />
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Right Panel - System Health */}
        <div className={clsx(
          "lg:block",
          activePanel === 'health' ? "block" : "hidden"
        )}>
          <SystemHealth selectedTenant={selectedTenant} channel={channel} />
        </div>
      </div>
    </div>
  );
};

export default AIControlRoomAdaptive;